<template>
  <div>
    <div>
      <div
        class="justify-center row items-center q-mx-xs"
      >
        <div
          v-if="!connectionRequested && !connectionEstablished && !data.length"
          style="position: fixed; align-items: center;"
          class="column"
        >
          <q-icon
            name="check_box"
            color="primary"
            size="xl"
          />
          <span class="text-h6">Ready</span>
        </div>
        <div
          v-else-if="connectionRequested && !connectionEstablished"
          style="position: fixed; align-items: center;"
          class="column"
        >
          <q-icon
            name="pending"
            color="primary"
            size="xl"
          />
          <span class="text-h6">Connecting...</span>
        </div>

        <div
          ref="scroll"
          class="text-left scroll full-width"
          style="display: block; height: 600px;"
        >
          <div
            v-for="(message, index) in data"
            :key="index"
            class="full-width"
          >
            <div class="row full-width">
              <span
                v-if="message.timestamp"
                class="col-2"
              >{{ message.timestamp }}</span>
              <!-- eslint-disable vue/no-v-html -->
              <pre
                :class="message.timestamp ? 'col-10' : 'col-12'"
                :style="{
                  'overflow-wrap': 'anywhere',
                  'white-space': 'pre-line',
                  'border-radius': '5px',
                  'background': getColorForLevel(message.level)
                }"
                v-html="message.message"
              />
              <!-- eslint-enable vue/no-v-html -->
            </div>
            <div style="border: solid 0.2px; color: lightgray " />
          </div>
          <q-input
            v-model="command"
            class="full-width"
            :disable="!connectionEstablished"
            borderless
            dense
            prefix=">"
            @keyup.enter="sendCommand"
          />
        </div>
      </div>
      <div class="row justify-between q-mx-xs q-my-md">
        <q-toggle
          v-model="autoScroll"
          label="Auto Scroll"
        />
        <div>
          <q-btn
            icon="download"
            class="q-mr-md"
            @click="downloadLog"
          />
          <q-btn
            v-if="!connectionEstablished && !connectionRequested"
            label="Request Console"
            color="primary"
            @click="requestConnection"
          />
          <q-btn
            v-else-if="connectionRequested && !connectionEstablished"
            label="Cancel Request"
            color="warning"
            @click="stopViewing"
          />
          <q-btn
            v-else-if="connectionEstablished"
            label="Disconnect"
            color="negative"
            @click="stopViewing"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

const EXECUTED_COMMAND_LOG_LEVEL = 'command'

export default {
  name: 'ConsoleStream',
  props: {
    registerClient: {
      type: Object,
      required: false,
      default: null
    }
  },
  data () {
    return {
      connectionRequested: false,
      connectionEstablished: false,
      data: [],
      command: '',
      autoScroll: true
    }
  },
  computed: {
    title () {
      return `Viewing Register Station ${this.registerClient?.registerStation?.name}`
    }
  },
  watch: {
    data () {
      // Function to auto scroll to the bottom of the console stream
      if (this.autoScroll && this.$refs.scroll) {
        // Use nextTick to ensure the data has been rendered before calculating the scroll height
        this.$nextTick(() => {
          this.$refs.scroll.scrollTop = this.$refs.scroll.scrollHeight
        })
      }
    }
  },
  beforeUnmount () {
    // Stop the console stream when the component is destroyed
    // This will also notify the producer to stop sending data
    if (this.connectionRequested || this.connectionEstablished) {
      this.$stopConsoleStream()
    }
  },
  methods: {
    show (registerClient) {
      this.registerClient = registerClient
      this.shown = true
    },
    requestConnection () {
      // Request the producer to start sending data
      this.connectionRequested = true
      this.$startConsoleStream(this.registerClient.id, this.stopCallback, this.dataReceivedCallback)
    },
    stopViewing () {
      // Stop the console stream
      this.$stopConsoleStream()
      this.connectionRequested = false
      this.connectionEstablished = false
    },
    stopCallback (message = null) {
      // Callback function to handle when the console stream is stopped
      // This will notify the user if there is an error message and also reset the connection status
      this.connectionEstablished = false
      this.connectionRequested = false
      if (message) {
        this.$mh.showErrors([message])
      }
    },
    getColorForLevel (level) {
      // Color function to handle the color of the console message based on the log level
      switch (level) {
        case 'error':
          return 'lightpink'
        case 'warn':
          return 'lightyellow'
        case 'log':
          return 'white'
        case 'command':
          return 'lightgrey'
        default:
          return 'white'
      }
    },
    sendCommand () {
      // Function to send a command to the producer
      // Also logs the command in the console stream since this won't be received back from the producer
      this.$sendConsoleCommand(this.command)
      this.data.push({ level: EXECUTED_COMMAND_LOG_LEVEL, message: this.command })
      // Reset the command input to behave like a console
      this.command = ''
    },
    dataReceivedCallback (data) {
      if (!this.connectionEstablished) {
        // If this is the first data received, set the connection status to established
        // The producer will send data as soon as connection is established
        this.connectionEstablished = true
      }
      // Add the data received from the producer to the console stream
      // This is in the format of a hash with a message, timestamp and level
      // e.g. { message: 'Hello World', timestamp: '2021-01-01T00:00:00.000Z', level: 'log' }
      this.data.push(data)
    },
    downloadLog () {
      // Function to download the console stream log as a text file
      const filename = `${new Date().toISOString()}-${this.registerClient?.registerStation?.name}-${this.registerClient?.registerStation?.id}-log.txt`
      const text = this.data.map(message => `${message.timestamp ? message.timestamp + ': ' : 'Command Executed: '}${message.message}`)

      // To download a file, we need to create an anchor element and simulate a click event
      const element = document.createElement('a')
      element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text.join('\n')))
      element.setAttribute('download', filename)

      // Hide the element and append it to the body to simulate the click event
      element.style.display = 'none'
      document.body.appendChild(element)
      element.click()

      // Remove the element from the body
      document.body.removeChild(element)
    }
  }
}
</script>
